import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
    ContentFullEnrollmentProvider,
    ContentFullEnrollmentContext,
} from 'features/coordinator/components/useContentfulEnrollment';
import { getEnrollmentEntryById } from 'common/api/contentful/enrollment';
import { noop } from 'common/utils';
import { FamilyEnrollment } from 'common/api/contentful/models/Familyenrollment';
import { Routes } from 'routes';
import { useHistory } from 'react-router-dom';
import { LoadingOverlay } from 'components/loading-overlay/LoadingOverlay';
import { ActivityIndicator } from 'components/activity-indicator/ActivityIndicator';
import {
    RegistrationRequest,
    emptyRegistrationRequest,
} from 'common/api/auth/models/RegistrationRequest';
import { EnrollmentContentType } from 'features/coordinator/types';
import { Organization } from 'common/api/crm/organization/enrollment/models';
import { IS_FLIPGIVE } from 'common/env';
import { useFirebaseConfigValues } from 'hooks/useFirebaseConfigValues';
import { Value } from 'firebase/remote-config';
import { ExternalSocialProvider } from 'common/api/auth/socialRegistration';
import { useSocialRegistrationProviderDiscovery } from 'features/social-registration/useSocialRegistrationProviders';

type SocialProvider = 'google' | 'apple' | null;

interface FamilyEnrollmentContextProps {
    goToStep: (_: string) => void;
    goToNextStep: () => void;
    goToPreviousStep: () => void;
    stepHeaders?: FamilyEnrollment;
    loader: React.RefObject<LoadingOverlay | null> | null;
    // NOTE: Update this interface to something more appropriate 1/2/3.
    // We need to accumulate the user info until step 3
    userAccountInfo: RegistrationRequest;
    updateUserAccountInfo: React.Dispatch<React.SetStateAction<RegistrationRequest>>;
    organization: Organization | null;
    setOrganizationTo: React.Dispatch<React.SetStateAction<Organization | null>>;
    phoneId: string;
    setPhoneId: React.Dispatch<React.SetStateAction<string>>;
    phoneNumber: string;
    setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
    isEnrollmentTestVariant: boolean;
    isSocialRegistration: boolean;
    isSocialRegistrationEnabled: boolean;
    socialRegistrationProviders: ExternalSocialProvider[];
    socialRegistrationError: string | null;
    setSocialRegistrationError: React.Dispatch<React.SetStateAction<string | null>>;
    socialRegistrationExistingAccountProvider: SocialProvider;
    setSocialRegistrationExistingAccountProvider: React.Dispatch<
        React.SetStateAction<SocialProvider>
    >;
    isResumingSocialRegistration: boolean;
    setIsResumingSocialRegistration: React.Dispatch<React.SetStateAction<boolean>>;
}

const initialFamilyEnrollmentState = {
    goToStep: noop,
    goToNextStep: noop,
    goToPreviousStep: noop,
    stepHeaders: {} as FamilyEnrollment,
    userAccountInfo: emptyRegistrationRequest,
    updateUserAccountInfo: noop,
    loader: null,
    organization: null,
    setOrganizationTo: noop,
    phoneId: '',
    setPhoneId: noop,
    phoneNumber: '',
    setPhoneNumber: noop,
    isEnrollmentTestVariant: false,
    isSocialRegistration: false,
    isSocialRegistrationEnabled: false,
    socialRegistrationProviders: [],
    socialRegistrationError: null,
    setSocialRegistrationError: noop,
    socialRegistrationExistingAccountProvider: null,
    setSocialRegistrationExistingAccountProvider: noop,
    isResumingSocialRegistration: false,
    setIsResumingSocialRegistration: noop,
};

export const FamilyEnrollmentContext = React.createContext<FamilyEnrollmentContextProps>(
    initialFamilyEnrollmentState
);

const getTransitions = (isSocial: boolean): { [key: string]: string } => {
    if (isSocial) {
        return {
            [Routes.EnrollFamilyGetStarted]: Routes.EnrollFamilySocialEarningFor,
            [Routes.EnrollFamilySocialEarningFor]: Routes.EnrollFamilySecurityQuestions,
            [Routes.EnrollFamilySecurityQuestions]: Routes.EnrollFamilySetupRecoveryPhone,
            [Routes.EnrollFamilySetupRecoveryPhone]: Routes.EnrollFamilyVerifyRecoveryPhone,
        };
    }
    return {
        [Routes.EnrollFamilyGetStarted]: Routes.EnrollFamilyCreateAccount,
        [Routes.EnrollFamilyCreateAccount]: Routes.EnrollFamilyAccountDetails,
        [Routes.EnrollFamilyAccountDetails]: Routes.EnrollFamilySecurityQuestions,
        [Routes.EnrollFamilySecurityQuestions]: Routes.EnrollFamilySetupRecoveryPhone,
        [Routes.EnrollFamilySetupRecoveryPhone]: Routes.EnrollFamilyVerifyRecoveryPhone,
    };
};

const FamilyEnrollmentProvider = ({ children }: React.PropsWithChildren) => {
    // NOTE: grab the contentful data and transform it to something we want to provide
    // to the steps
    const [stepHeaders, setStepHeadersTo] = useState<FamilyEnrollment>();
    const { contentfulEntry, isLoading } = useContext(ContentFullEnrollmentContext);
    const isEnrollmentTestVariant = useFirebaseConfigValues(
        'participant_enrollment_test_web',
        false,
        (v: Value) => v.asBoolean()
    );
    const {
        providers: socialRegistrationProviders,
        isLoading: isLoadingSocialRegistrationProviders,
    } = useSocialRegistrationProviderDiscovery();
    const isSocialRegistrationEnabled = socialRegistrationProviders.length > 0;
    const [userAccountInfo, setUserAccountInfoTo] =
        useState<RegistrationRequest>(emptyRegistrationRequest);
    const [organization, setOrganizationTo] = useState<Organization | null>({} as Organization);
    const [socialRegistrationError, setSocialRegistrationError] = useState<string | null>(null);
    const [
        socialRegistrationExistingAccountProvider,
        setSocialRegistrationExistingAccountProvider,
    ] = useState<'google' | 'apple' | null>(null);
    const [isResumingSocialRegistration, setIsResumingSocialRegistration] = useState(false);
    const loader = useRef<LoadingOverlay>(null);
    const history = useHistory();

    const isSocialRegistration =
        userAccountInfo.registrationMethod === 'social' &&
        Boolean(userAccountInfo.externalRegistrationKey);

    const goToNextStep = useCallback(() => {
        const path = history.location.pathname;
        const transitions = getTransitions(isSocialRegistration);
        const nextStep = transitions[path];

        const currentParams = new URLSearchParams(window.location.search);
        history.push({
            pathname: nextStep,
            search: `?${currentParams.toString()}`,
        });
    }, [history, isSocialRegistration]);

    const goToPreviousStep = () => history.goBack();

    const goToStep = useCallback(
        (path: string) => {
            // NOTE: might not need it
            const currentParams = new URLSearchParams(window.location.search);
            history.replace({
                pathname: path,
                search: `?${currentParams.toString()}`,
            });
        },
        [history]
    );

    useEffect(() => {
        if (contentfulEntry) {
            setStepHeadersTo(contentfulEntry as FamilyEnrollment);
        }
    }, [contentfulEntry]);

    // Default marketingOptIn: false for Canadian orgs/FlipGive, true for Americans (emptyRegistrationRequest)
    useEffect(() => {
        const isCanadian = organization?.countryCode === 'CA' || IS_FLIPGIVE;

        if (isCanadian) {
            setUserAccountInfoTo((prev) =>
                prev.email ? prev : { ...prev, marketingOptIn: false }
            );
        }
    }, [organization?.countryCode, IS_FLIPGIVE]);

    const [phoneId, setPhoneId] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');

    const context = {
        goToNextStep,
        goToPreviousStep,
        goToStep,
        stepHeaders: stepHeaders,
        loader,
        userAccountInfo,
        updateUserAccountInfo: setUserAccountInfoTo,
        organization,
        setOrganizationTo,
        phoneId,
        setPhoneId,
        phoneNumber,
        setPhoneNumber,
        isEnrollmentTestVariant: isEnrollmentTestVariant ?? false,
        isSocialRegistration,
        isSocialRegistrationEnabled,
        socialRegistrationProviders,
        socialRegistrationError,
        setSocialRegistrationError,
        socialRegistrationExistingAccountProvider,
        setSocialRegistrationExistingAccountProvider,
        isResumingSocialRegistration,
        setIsResumingSocialRegistration,
    };

    // make sure we only render IFF contentful has loaded
    if (isLoading || isLoadingSocialRegistrationProviders) {
        return (
            <div className="mt-12 justify-center flex">
                <ActivityIndicator />
            </div>
        );
    }

    return (
        <FamilyEnrollmentContext.Provider value={context}>
            {children}
            <LoadingOverlay ref={loader} />
        </FamilyEnrollmentContext.Provider>
    );
};

export const FamilyEnrollmentView = ({ children }: React.PropsWithChildren) => {
    return (
        <ContentFullEnrollmentProvider
            enrollmentVersion="v1"
            firebaseKey={'family_enrollment_contentful_heading_map'}
            fetchContentfulContent={getEnrollmentEntryById}
            contentType={EnrollmentContentType.FAMILY_ENROLLMENT}
        >
            <FamilyEnrollmentProvider>{children}</FamilyEnrollmentProvider>
        </ContentFullEnrollmentProvider>
    );
};
